import React, { useState, useRef, useEffect } from "react";
import { Sparkles, X, Send, Loader2, AlertCircle } from "lucide-react";
import { AI, apiErrorMessage } from "../lib/api";
import { useApp } from "../context/AppContext";
import { cn } from "../lib/utils";

/**
 * Floating "Ask AI" FAB (bottom-right) — available on every page for any
 * logged-in user. Backend enforces role-scoped access: users only get
 * answers based on sales they can already see via REST.
 *
 * Kept intentionally single-turn (no history) for MVP. Add multi-turn
 * later by threading a session_id through AI.ask.
 */

const QUICK_PROMPTS = [
  "How many sales this month?",
  "Which DSE has the most sales?",
  "List sales pending payment",
  "Show cash vs finance split",
];

export default function AskAiButton() {
  const { activeUser } = useApp();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  if (!activeUser) return null;

  const submit = async (q) => {
    const text = (q ?? question).trim();
    if (!text || loading) return;
    setLoading(true);
    setError("");
    setAnswer("");
    setQuestion(text);
    try {
      const { answer: a } = await AI.ask(text);
      setAnswer(a || "(no response)");
    } catch (e) {
      setError(apiErrorMessage(e) || "AI request failed");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full",
          "bg-gradient-to-br from-primary to-primary/80 px-4 py-3 text-sm font-semibold text-white",
          "shadow-lg shadow-primary/30 ring-1 ring-primary/40 transition-all",
          "hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5",
          open && "opacity-0 pointer-events-none",
        )}
        data-testid="ask-ai-fab"
        title="Ask AI (GPT 5.6 Sol)"
      >
        <Sparkles className="h-4 w-4" />
        <span className="hidden sm:inline">Ask AI</span>
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 flex w-[min(420px,calc(100vw-3rem))] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
          style={{ maxHeight: "min(70vh, 640px)" }}
          data-testid="ask-ai-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-br from-primary/5 to-transparent px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                <Sparkles className="h-4 w-4 text-primary" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">Ask AI</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">
                  GPT 5.6 Sol · your data only
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
              data-testid="ask-ai-close"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {!answer && !loading && !error && (
              <>
                <p className="text-xs text-slate-500">
                  Ask a question about your sales, tasks, deliveries or team performance.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_PROMPTS.map((p) => (
                    <button
                      key={p}
                      onClick={() => submit(p)}
                      className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] text-slate-700 hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
                      data-testid={`ask-ai-quick-${p.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </>
            )}
            {loading && (
              <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Thinking…
              </div>
            )}
            {error && (
              <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            {answer && (
              <div
                className="whitespace-pre-wrap break-words rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-sm leading-relaxed text-slate-900"
                data-testid="ask-ai-answer"
              >
                {answer}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-100 bg-white p-2">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={onKeyDown}
                rows={2}
                placeholder="Ask anything about your sales…"
                className="flex-1 resize-none rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/20"
                data-testid="ask-ai-input"
              />
              <button
                onClick={() => submit()}
                disabled={loading || !question.trim()}
                className="rounded-md bg-primary p-2 text-white transition-opacity disabled:opacity-40"
                data-testid="ask-ai-send"
                aria-label="Send"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
